
"use client";

import React from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { LayoutDashboard, BookOpenCheck, Tablet, Maximize, Minimize, LogOut, Settings, ListChecks, Store } from "lucide-react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "./ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useKioskMode } from "@/contexts/KioskModeContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Logo } from "./Logo";

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const { logout, appUser, userStores, currentStoreId, setCurrentStoreId, firebaseUser } = useAuth();
  const { enterKioskMode } = useKioskMode();
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const navLinks = [
    { href: "/order", label: "Order", icon: BookOpenCheck, roles: ['Staff', 'Manager', 'Admin', 'Driver'] },
    { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ['Manager', 'Admin'] },
    { href: "/kds", label: "KDS", icon: ListChecks, roles: ['Staff', 'Manager', 'Admin'] },
  ];
  
  const userRole = currentStoreId && appUser ? appUser.accessibleStores[currentStoreId] : null;
  const visibleLinks = navLinks.filter(link => userRole && link.roles.includes(userRole));
  
  const currentStoreName = userStores.find(s => s.id === currentStoreId)?.name;

  React.useEffect(() => {
    const handleFullscreenChange = () => {
       if (typeof document !== 'undefined') {
          setIsFullscreen(!!document.fullscreenElement);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    handleFullscreenChange(); // Check initial state
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  
  const toggleFullScreen = () => {
    if (typeof document === 'undefined') return;
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Could not enable full-screen mode: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
    }
  };

  const handleGoToKiosk = () => {
    enterKioskMode();
    router.push('/kiosk');
  };

  if (!firebaseUser) return null;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 max-w-screen-2xl items-center">
        <Link href="/dashboard" className="mr-6 flex items-center space-x-2">
          <Logo className="h-10 w-10" />
          <span className="font-headline text-2xl font-bold inline-block text-foreground">
            OrderPoint<sup className="text-primary ml-1">®️</sup>
          </span>
        </Link>
        
        {currentStoreId && userStores.length > 0 && (
           <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="hidden md:flex">
                  <Store className="mr-2 h-4 w-4" />
                  <span className="truncate max-w-[150px]">{currentStoreName || "Select Store"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Your Stores</DropdownMenuLabel>
                {userStores.map(store => (
                  <DropdownMenuItem key={store.id} onClick={() => setCurrentStoreId(store.id)} disabled={store.id === currentStoreId}>
                    {store.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
        )}

        <nav className="flex items-center gap-4 lg:gap-6 ml-4">
          {visibleLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-primary flex items-center gap-2",
                  pathname.startsWith(link.href) ? "text-primary" : "text-muted-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden md:inline">{link.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="ml-auto flex items-center gap-2">
           {currentStoreName && (
            <div className="hidden lg:flex items-center gap-2 border-r pr-4 mr-2">
              <Store className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">{currentStoreName}</span>
            </div>
           )}
           <Button variant="outline" size="sm" onClick={handleGoToKiosk}>
            <Tablet className="mr-2 h-4 w-4" />
            Kiosk Mode
          </Button>
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                 <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{appUser?.fullName || appUser?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleFullScreen}>
                {isFullscreen ? <Minimize className="mr-2 h-4 w-4" /> : <Maximize className="mr-2 h-4 w-4" />}
                <span>Toggle Fullscreen</span>
              </DropdownMenuItem>
               <DropdownMenuItem onClick={() => router.push('/login-pin')}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Switch POS User</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
